import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { isInDepartment } from '@/utils/departments';
import type { ChatTarget } from '../types';

const MEMBER_POLL_MS = 30000;

type DepartmentChatRow = { event_id: string; department: string };

/**
 * Who counts as a member of this thread, for the "seen by everyone" tick.
 *
 * Event chat: the creator plus active volunteers, polled because the roster changes.
 * Department chat: the creator plus the active volunteers assigned to that department,
 * polled for the same reason -- reassignment changes the roster underneath us.
 * Direct chat: exactly the two participants, already known from the direct_chats row,
 * so no query and no polling.
 */
export function useChatMemberIds(target: ChatTarget, directParticipantIds?: string[]) {
  const [memberIds, setMemberIds] = useState<string[]>([]);

  const eventId = target.kind === 'event' ? target.eventId : null;
  const departmentChatId = target.kind === 'department' ? target.departmentChatId : null;
  const directIdsKey = directParticipantIds?.join(',') ?? '';

  /** Event creator + the active volunteers on it, optionally narrowed to one department. */
  const loadEventRoster = useCallback(async (id: string, department: string | null) => {
    const [{ data: eventRow, error: eventError }, { data: volunteerRows, error: volunteerError }] =
      await Promise.all([
        supabase.from('events').select('created_by').eq('id', id).maybeSingle(),
        supabase
          .from('volunteers')
          .select('volunteer_id, department')
          .eq('event_id', id)
          .eq('is_active', true),
      ]);

    if (eventError) {
      console.error('[EventChat] Failed to load event creator for read receipts', eventError);
    }
    if (volunteerError) {
      console.error('[EventChat] Failed to load event volunteers for read receipts', volunteerError);
    }

    const ids = new Set<string>();
    const createdBy = (eventRow as { created_by?: string } | null)?.created_by;
    if (createdBy) ids.add(createdBy);
    for (const row of volunteerRows ?? []) {
      const { volunteer_id: volunteerId, department: raw } = row as {
        volunteer_id: string;
        department: string | null;
      };
      if (!volunteerId) continue;
      // Mirrors can_access_department_chat's volunteer arm. Filtering here rather than in
      // the query because the column is a comma-separated list, not a relation.
      if (department != null && !isInDepartment(raw, department)) continue;
      ids.add(volunteerId);
    }
    setMemberIds([...ids]);
  }, []);

  const refetch = useCallback(async () => {
    if (eventId) {
      await loadEventRoster(eventId, null);
      return;
    }

    if (departmentChatId) {
      const { data, error } = await supabase
        .from('department_chats')
        .select('event_id, department')
        .eq('id', departmentChatId)
        .maybeSingle();
      if (error || !data) {
        if (error) {
          console.error('[EventChat] Failed to load department chat for read receipts', error);
        }
        setMemberIds([]);
        return;
      }
      const row = data as DepartmentChatRow;
      await loadEventRoster(row.event_id, row.department);
      return;
    }

    setMemberIds(directIdsKey ? directIdsKey.split(',') : []);
  }, [eventId, departmentChatId, directIdsKey, loadEventRoster]);

  useEffect(() => {
    void refetch();

    // A direct thread's two participants are fixed for the life of the thread, so there
    // is nothing to poll for -- only an event roster, or a department's slice of one,
    // changes underneath us.
    if (!eventId && !departmentChatId) return;

    const pollId = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refetch();
    }, MEMBER_POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refetch();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(pollId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refetch, eventId, departmentChatId]);

  return { memberIds };
}
